<template>
  <form>
    <br />
    <center>
      <h1 v-if="!isPositionQuestionActive()" class="title">
        Question {{ activeStep }}
      </h1>
      <h1 v-else class="title">Question {{ activeStep + 1 }}</h1>
    </center>
    <br />
    <div class="columns">
      <div class="column is-6 is-offset-3">
        <b-progress size="is-small" type="is-primary" :value="progressValue" />
      </div>
    </div>
    <center>
      <h6 class="subtitle is-6"><strong v-html="activeQuestion"></strong></h6>
    </center>
    <br />
    <b-notification
      v-if="!position && activeStep === 0"
      :type="errors.has('position') ? 'is-danger' : ''"
      :closable="false"
    >
      <div v-for="(position, index) in positions" :key="index">
        <b-radio
          :type="errors.has('position') ? 'is-danger' : ''"
          @input="$emit('input', buffer)"
          :name="position"
          v-validate="'required'"
          v-model="buffer.position"
          :native-value="position"
        >
          {{ $t(position) }}
        </b-radio>
      </div>
      <b-field
        :type="{ 'is-danger': errors.has('position') }"
        :message="errors.first('position')"
      />
    </b-notification>
    <b-notification
      v-if="activeStep === 1 && surveyTagCategories.length > 0"
      :type="errors.has('tags') ? 'is-danger' : ''"
      :closable="false"
    >
      <tags-input
        v-model="buffer.tags"
        :required="true"
        :tagCategories="surveyTagCategories"
      />
    </b-notification>
    <b-notification
      v-if="
        activeStep === 2 ||
        (activeStep === 1 && surveyTagCategories.length === 0)
      "
      :closable="false"
      :type="errors.has('nWorkingDay') ? 'is-danger' : ''"
    >
      <b-field
        :type="{ 'is-danger': errors.has('nWorkingDay') }"
        :message="errors.first('nWorkingDay')"
      >
        <b-select
          name="nWorkingDay"
          expanded
          v-validate="'required'"
          :placeholder="$t('compCarbonFormsSurveyForm.placeholder-2') + ' *'"
          v-model="buffer.nWorkingDay"
          @input="$emit('input', buffer)"
          data-testid="nWorkingDay"
        >
          <option
            v-for="(ndays, index) in [0, 1, 2, 3, 4, 5, 6, 7]"
            :key="index"
            :value="ndays"
          >
            <span v-if="[0, 1].includes(ndays)"
              >{{ ndays }}
              {{ $t('compCarbonFormsSurveyForm.day-per-week') }}</span
            >
            <span v-else
              >{{ ndays }}
              {{ $t('compCarbonFormsSurveyForm.days-per-week') }}</span
            >
          </option>
        </b-select>
      </b-field>
      <br />
      <strong>« {{ $t('compCarbonFormsSurveyForm.workplace-1') }} »</strong> =
      {{ $t('compCarbonFormsSurveyForm.workplace-2') }}
      <br />
      <em>{{ $t('compCarbonFormsSurveyForm.workplace-3') }}</em>
    </b-notification>
    <div class="columns">
      <div class="column">
        <b-button
          class="button is-fullwidth"
          :disabled="previousDisabled"
          @click="previousStep"
          data-testid="previous-step"
          icon-left="angle-double-left"
        >
          {{ $t('previous') }}
        </b-button>
      </div>
      <div class="column">
        <b-button
          class="button is-fullwidth is-primary"
          @click="nextStep"
          data-testid="next-step-survey"
          icon-right="angle-double-right"
        >
          {{ nextBtnLabel }}
        </b-button>
      </div>
    </div>
  </form>
</template>

<script>
import { useCoreStore } from '@/stores/core.js'

export default {
  name: 'Form',
  props: {
    value: {
      type: Object,
      default: function () {
        return {}
      }
    },
    surveyTagCategories: {
      type: Array,
      default: function () {
        return []
      }
    },
    year: {
      type: Number,
      default: new Date().getFullYear() - 1
    },
    isSurvey: {
      type: Boolean,
      default: false
    },
    isSimulation: {
      type: Boolean,
      default: false
    },
    totalStep: {
      type: Number,
      default: 2
    }
  },
  $_veeValidate: {
    validator: 'new'
  },
  components: {
    'tags-input': () => import('@/components/core/TagsInput.vue')
  },
  data () {
    return {
      activeStep: 0,
      buffer: Object.assign({}, this.value),
      position: ''
    }
  },
  setup () {
    const coreStore = useCoreStore()
    return { coreStore }
  },
  computed: {
    positions () {
      return this.coreStore.surveyPositionTitles
    },
    nextBtnLabel: function () {
      if (
        !this.isSimulation &&
        this.isSurvey &&
        this.buffer.nWorkingDay === 0 &&
        this.activeStep > 1
      ) {
        return this.$t('preview')
      }
      if (this.isSimulation && this.buffer.nWorkingDay === 0) {
        return this.$t('add-simu')
      }
      if (
        !this.isSimulation &&
        !this.isSurvey &&
        this.buffer.nWorkingDay === 0
      ) {
        return this.$t('add')
      } else {
        return this.$t('next')
      }
    },
    progressValue: function () {
      if (!this.isPositionQuestionActive()) {
        return (this.activeStep / this.totalStep) * 100
      } else {
        return ((this.activeStep + 1) / this.totalStep) * 100
      }
    },
    previousDisabled: function () {
      if (!this.isPositionQuestionActive()) {
        return this.activeStep === 1
      } else {
        return this.activeStep === 0
      }
    },
    activeQuestion: function () {
      if (this.activeStep === 0) {
        return this.$t('compCarbonFormsSurveyForm.question-1')
      } else if (this.activeStep === 1 && this.surveyTagCategories.length > 0) {
        return this.$t('compCarbonFormsSurveyForm.question-2')
      } else if (
        this.activeStep === 2 ||
        (this.activeStep === 1 && this.surveyTagCategories.length === 0)
      ) {
        let label =
          this.$t('compCarbonFormsSurveyForm.question-3-1') +
          ' ' +
          this.year +
          ', '
        if (this.year === 2020 || this.year === 2021) {
          label += this.$t('compCarbonFormsSurveyForm.question-3-loked')
        } else {
          label += this.$t('compCarbonFormsSurveyForm.question-3-2')
        }
        return label + ' *'
      } else {
        return ''
      }
    }
  },
  methods: {
    nextStep () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          if (this.activeStep === 1 && this.surveyTagCategories.length === 0) {
            this.$emit('submit')
          } else if (this.activeStep === 2) {
            this.$emit('submit')
          } else {
            this.activeStep += 1
          }
        }
      })
    },
    previousStep () {
      this.activeStep -= 1
    },
    isPositionQuestionActive () {
      let isActive = true
      if (this.isSimulation) {
        isActive = false
      } else if (this.positions.length === 1) {
        isActive = false
      }
      return isActive
    }
  },
  watch: {
    value: {
      handler () {
        this.buffer = Object.assign({}, this.value)
      },
      deep: true
    }
  },
  mounted () {
    if (this.positions.length === 1) {
      this.position = this.positions[0]
      this.activeStep = 1
    }
    if (this.position) {
      this.buffer.position = this.position
      this.$emit('input', this.buffer)
    }
    if (this.isSimulation) {
      this.activeStep = 1
    }
  }
}
</script>

<style>
.notification.is-danger {
  background-color: #fbf4f6 !important;
}
</style>
